import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => Promise<void>;
    isDeleting?: boolean;
};

const SuperAdminBookingDeleteDialog = ({ open, onOpenChange, onConfirm, isDeleting }: Props) => {
    const { t } = useTranslation();
    const [confirmText, setConfirmText] = useState('');
    const confirmWord = t('superAdmin.data.bookings.delete.confirmWord');

    const handleConfirm = async () => {
        await onConfirm();
        setConfirmText('');
        onOpenChange(false);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            setConfirmText('');
        }
        onOpenChange(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('superAdmin.data.bookings.delete.title')}</DialogTitle>
                    <DialogDescription>
                        {t('superAdmin.data.bookings.delete.description')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-1.5">
                    <Label>{t('superAdmin.data.bookings.delete.confirmLabel')}</Label>
                    <Input
                        value={confirmText}
                        onChange={(event) => setConfirmText(event.target.value)}
                        placeholder={t('superAdmin.data.bookings.delete.confirmPlaceholder')}
                    />
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)}>
                        {t('elements.buttons.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={confirmText !== confirmWord || isDeleting}
                        onClick={() => void handleConfirm()}
                    >
                        {t('superAdmin.data.bookings.delete.confirmAction')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SuperAdminBookingDeleteDialog;
